// /src/admin/AdminTextBanners.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
  query,
  Timestamp,
  where,
  getDocs,
} from "firebase/firestore";
import app from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";
const db = getFirestore(app);

// Helpers datetime-local <-> Timestamp
function toLocalInputValue(tsOrDate) {
  try {
    const d =
      tsOrDate?.toDate
        ? tsOrDate.toDate()
        : tsOrDate instanceof Date
        ? tsOrDate
        : new Date(tsOrDate);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}
function fromLocalInputValue(str) {
  if (!str) return null;
  const d = new Date(str);
  if (isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

export default function AdminTextBanners() {
  const { cuerpoId } = useCuerpo();
  const [companias, setCompanias] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Form ----
  const [editId, setEditId] = useState(null);
  const [cia, setCia] = useState("");
  const [title, setTitle] = useState("");
  const [obac, setObac] = useState("");
  const [maquinista, setMaquinista] = useState("");
  const [bomberosSel, setBomberosSel] = useState([]); // array de nombres
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [order, setOrder] = useState(1);
  const [titleColor, setTitleColor] = useState("#16a34a");
  const [obacColor, setObacColor] = useState("#dc2626");
  const [maquinistaColor, setMaquinistaColor] = useState("#dc2626");
  const [bomberosColor, setBomberosColor] = useState("#dc2626");

  // ---- Lista de bomberos por compañía ----
  const [bomberosCia, setBomberosCia] = useState([]); // [{id,nombre}, ...]

  useEffect(() => {
    // Suscribe banners (sin orderBy; ordenamos en memoria)
    const unsub = onSnapshot(
      query(collection(db, "textBanners"), where("cuerpo_id", "==", cuerpoId)),
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        arr.sort((a, b) => {
          const as = a.startAt?.toDate
            ? a.startAt.toDate().getTime()
            : new Date(a.startAt).getTime();
          const bs = b.startAt?.toDate
            ? b.startAt.toDate().getTime()
            : new Date(b.startAt).getTime();
        return (as || 0) - (bs || 0);
        });
        setItems(arr);
        setLoading(false);
      },
      (err) => {
        console.error("onSnapshot textBanners:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [cuerpoId]);

  useEffect(() => {
    if (!cuerpoId) return;
    (async()=>{
      const snap = await getDocs(query(collection(db,"companias"), where("cuerpo_id","==",cuerpoId)));
      const arr=snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>Number(a.orden||999)-Number(b.orden||999));
      setCompanias(arr);
      if (!cia && arr.length) setCia(arr[0].nombre || "");
    })().catch(console.error);
  }, [cuerpoId]);

  // Cargar bomberos de la compañía seleccionada
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "bomberos"), where("cuerpo_id", "==", cuerpoId)));
        if (!mounted) return;
        const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter(b => b.compania === cia);
        arr.sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
        setBomberosCia(arr);
      } catch (e) {
        console.error("Error cargando bomberos de la compañía:", e);
        setBomberosCia([]);
      }
    })();
    return () => { mounted = false; };
  }, [cia, cuerpoId]);

  const activeNow = useMemo(() => {
    const now = Date.now();
    return items.filter((it) => {
      const s = it.startAt?.toDate
        ? it.startAt.toDate().getTime()
        : new Date(it.startAt).getTime();
      const e = it.endAt?.toDate
        ? it.endAt.toDate().getTime()
        : new Date(it.endAt).getTime();
      const okCia = !it.cia || it.cia === cia; // solo por visualizar… (la tabla muestra todos)
      return !isNaN(s) && !isNaN(e) && now >= s && now < e && it.enabled && okCia;
    });
  }, [items, cia]);

  function resetForm() {
    setEditId(null);
    setTitle("");
    setObac("");
    setMaquinista("");
    setBomberosSel([]);
    setStartAt("");
    setEndAt("");
    setEnabled(true);
    setOrder(1);
    setTitleColor("#16a34a");
    setObacColor("#dc2626");
    setMaquinistaColor("#dc2626");
    setBomberosColor("#dc2626");
    // Cia la dejamos como la actual seleccionada
  }

  async function save(e) {
    e.preventDefault();
    try {
      const payload = {
        cia,
        cuerpo_id: cuerpoId,
        title: title.trim(),
        obac: obac || "",
        maquinista: maquinista || "",
        bomberos: bomberosSel || [],
        startAt: fromLocalInputValue(startAt) ?? Timestamp.fromDate(new Date()),
        endAt:
          fromLocalInputValue(endAt) ??
          Timestamp.fromDate(new Date(Date.now() + 86400000)),
        enabled: Boolean(enabled),
        order: Number(order) || 0,
        titleColor: titleColor || "#16a34a",
        obacColor: obacColor || "#dc2626",
        maquinistaColor: maquinistaColor || obacColor || "#dc2626",
        bomberosColor: bomberosColor || obacColor || "#dc2626",
      };
      if (!payload.title) throw new Error("El título es obligatorio.");

      if (editId) {
        await setDoc(doc(db, "textBanners", editId), payload, { merge: true });
      } else {
        await addDoc(collection(db, "textBanners"), payload);
      }
      resetForm();
    } catch (err) {
      console.error("Error guardando banner:", err);
      alert(`Error guardando banner: ${err.message || err}`);
    }
  }

  function editRow(it) {
    setEditId(it.id);
    setCia(it.cia || companias[0]?.nombre || "");
    setTitle(it.title || "");
    setObac(it.obac || "");
    setMaquinista(it.maquinista || "");
    setBomberosSel(Array.isArray(it.bomberos) ? it.bomberos : []);
    setStartAt(toLocalInputValue(it.startAt));
    setEndAt(toLocalInputValue(it.endAt));
    setEnabled(!!it.enabled);
    setOrder(it.order ?? 1);
    setTitleColor(it.titleColor || "#16a34a");
    setObacColor(it.obacColor || "#dc2626");
    setMaquinistaColor(it.maquinistaColor || it.obacColor || "#dc2626");
    setBomberosColor(it.bomberosColor || it.obacColor || "#dc2626");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function removeRow(id) {
    if (!window.confirm("¿Eliminar este banner?")) return;
    try {
      await deleteDoc(doc(db, "textBanners", id));
      if (id === editId) resetForm();
    } catch (err) {
      console.error("Error eliminando:", err);
      alert("No se pudo eliminar.");
    }
  }

  async function toggleEnabled(it) {
    try {
      await setDoc(doc(db, "textBanners", it.id), { enabled: !it.enabled }, { merge: true });
    } catch (err) {
      console.error("Error actualizando enabled:", err);
    }
  }

  // opciones de bomberos (nombre)
  const bomberoOpts = bomberosCia.map((b) => b.nombre).filter(Boolean);

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Banners de Texto (debajo del logo)</h2>
      <p style={{ marginTop: -8, color: "#555" }}>
        Programa mensajes por rango de fechas/horas. Selecciona la <strong>compañía</strong> para filtrar el personal.
      </p>

      {/* Compañía */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontWeight: 600, marginRight: 8 }}>Compañía</label>
        <select value={cia} onChange={(e) => { setCia(e.target.value); setObac(""); setMaquinista(""); setBomberosSel([]); }}>
          {companias.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
        </select>
      </div>

      <form className="formulario" onSubmit={save}>
        <input
          type="text"
          placeholder="Título (ej: Beneficio Acuenta 01-11-2025)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        {/* Selects de personal */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>OBAC</span>
            <select value={obac} onChange={(e) => setObac(e.target.value)}>
              <option value="">-- Seleccionar --</option>
              {bomberoOpts.map((n) => <option key={`obac-${n}`} value={n}>{n}</option>)}
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>Maquinista</span>
            <select value={maquinista} onChange={(e) => setMaquinista(e.target.value)}>
              <option value="">-- Seleccionar --</option>
              {bomberoOpts.map((n) => <option key={`maq-${n}`} value={n}>{n}</option>)}
            </select>
          </label>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span>Bomberos (mantén presionada tecla Ctrl/⌘ para selección múltiple)</span>
          <select
            multiple
            value={bomberosSel}
            onChange={(e) => setBomberosSel(Array.from(e.target.selectedOptions).map(o => o.value))}
            style={{ minHeight: 110 }}
          >
            {bomberoOpts.map((n) => <option key={`bom-${n}`} value={n}>{n}</option>)}
          </select>
        </label>

        {/* Fechas */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>Inicio (fecha y hora)</span>
            <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} required />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>Término (fecha y hora)</span>
            <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} required />
          </label>
        </div>

        {/* Estado / Orden / Colores */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Habilitado
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            Orden
            <input type="number" value={order} onChange={(e) => setOrder(e.target.value)} min={0} style={{ width: 100 }} />
          </label>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Color título
              <input type="color" value={titleColor} onChange={(e) => setTitleColor(e.target.value)} />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Color OBAC
              <input type="color" value={obacColor} onChange={(e) => setObacColor(e.target.value)} />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Color Maquinista
              <input type="color" value={maquinistaColor} onChange={(e) => setMaquinistaColor(e.target.value)} />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Color Bomberos
              <input type="color" value={bomberosColor} onChange={(e) => setBomberosColor(e.target.value)} />
            </label>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit">{editId ? "Guardar cambios" : "Crear banner"}</button>
          {editId && (
            <button type="button" onClick={resetForm} style={{ background: "#7f8c8d" }}>
              Cancelar edición
            </button>
          )}
        </div>
      </form>

      {/* Listado */}
      <h3 style={{ marginTop: "2rem" }}>Listado</h3>
      {loading ? (
        <p>Cargando…</p>
      ) : items.length === 0 ? (
        <p>No hay banners.</p>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th>Compañía</th>
              <th>Título</th>
              <th>OBAC</th>
              <th>Maquinista</th>
              <th>Bomberos</th>
              <th>Inicio</th>
              <th>Fin</th>
              <th>Habilitado</th>
              <th>Activo ahora</th>
              <th>Orden</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const now = Date.now();
              const s = it.startAt?.toDate ? it.startAt.toDate().getTime() : new Date(it.startAt).getTime();
              const e = it.endAt?.toDate ? it.endAt.toDate().getTime() : new Date(it.endAt).getTime();
              const active = !isNaN(s) && !isNaN(e) && now >= s && now < e && it.enabled;
              const fmt = (x) => (x?.toDate ? x.toDate().toLocaleString() : new Date(x).toLocaleString());
              return (
                <tr key={it.id}>
                  <td>{it.cia || "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td>{it.obac || "-"}</td>
                  <td>{it.maquinista || "-"}</td>
                  <td>{Array.isArray(it.bomberos) && it.bomberos.length ? it.bomberos.join(", ") : "-"}</td>
                  <td>{fmt(it.startAt)}</td>
                  <td>{fmt(it.endAt)}</td>
                  <td>{it.enabled ? "Sí" : "No"}</td>
                  <td>{active ? "Sí" : "No"}</td>
                  <td>{it.order ?? 0}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button type="button" onClick={() => editRow(it)} style={{ background: "#2980b9" }}>Editar</button>
                      <button type="button" onClick={() => toggleEnabled(it)} style={{ background: it.enabled ? "#e67e22" : "#27ae60" }}>
                        {it.enabled ? "Deshabilitar" : "Habilitar"}
                      </button>
                      <button type="button" onClick={() => removeRow(it.id)} style={{ background: "#c0392b" }}>Eliminar</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
